/**
 * Вид «Требуют внимания».
 *
 * Две группы, и они не пересекаются: срок идёт, но к задаче не приступили,
 * и срок вышел, а задача не закрыта. Тот же отчёт присылает бот, считается
 * он на сервере в одном месте — цифры обязаны совпадать.
 *
 * Фильтры сюда не применяются: это отчёт, а не срез списка.
 */
import { Badge, Card, Icon } from '@/ds';
import { EmptyState } from '@/components/EmptyState';
import { Provenance } from '@/components/money';
import { useAttention, type AttentionTask } from '@/api/hooks';
import { formatDate, formatDateShort, plural } from '@/lib/format';
import { STATUS_LABELS } from './shared';
import { QueryState } from '@/components/QueryState';

function daysLabel(days: number): string {
  if (days === 0) return 'сегодня';
  return `${days} ${plural(days, 'день', 'дня', 'дней')}`;
}

function TaskRowView({
  task,
  kind,
  headLabel,
}: {
  task: AttentionTask;
  kind: 'overdue' | 'not_started';
  headLabel: string;
}) {
  const due = task.dueDateEnd
    ? `${formatDateShort(task.dueDate ?? '')} — ${formatDateShort(task.dueDateEnd)}`
    : formatDateShort(task.dueDate ?? '');

  return (
    <tr>
      <td data-label={headLabel} style={{ width: 96 }}>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--fs-xs)',
            color: kind === 'overdue' ? 'var(--status-danger-fg)' : 'var(--status-warning-fg)',
            whiteSpace: 'nowrap',
          }}
        >
          {kind === 'overdue' ? `+${daysLabel(task.days)}` : daysLabel(task.days)}
        </span>
      </td>
      <td data-label="Задача">
        <a
          href={task.notionUrl}
          target="_blank"
          rel="noreferrer"
          style={{ color: 'var(--text-strong)', fontWeight: 500 }}
        >
          {task.title}
        </a>
        {task.projectName ? <Provenance>{task.projectName}</Provenance> : null}
      </td>
      <td data-label="Ответственный" style={{ color: 'var(--text-muted)' }}>
        {task.assignees.length > 0
          ? task.assignees.map((person) => person.name).join(', ')
          : 'без ответственного'}
      </td>
      <td data-label="Статус">
        <Badge tone={task.status === 'in_progress' ? 'brand' : 'neutral'}>
          {STATUS_LABELS[task.status]}
        </Badge>
      </td>
      <td data-label="Срок" className="kj-mono">{due}</td>
    </tr>
  );
}

function Group({
  title,
  explanation,
  tasks,
  kind,
  headColumn,
}: {
  title: string;
  explanation: string;
  tasks: AttentionTask[];
  kind: 'overdue' | 'not_started';
  headColumn: string;
}) {
  if (tasks.length === 0) return null;

  return (
    <Card padding="md">
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
          marginBottom: 'var(--space-2)',
        }}
      >
        <span
          style={{
            font: '500 16px var(--font-display)',
            letterSpacing: 'var(--ls-heading)',
            color: 'var(--text-strong)',
          }}
        >
          {title}
        </span>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 20,
            color: kind === 'overdue' ? 'var(--status-danger-fg)' : 'var(--status-warning-fg)',
          }}
        >
          {tasks.length}
        </span>
      </div>

      <Provenance>{explanation}</Provenance>

      <div className="kj-scroll-x" style={{ marginTop: 'var(--space-4)' }}>
        <table className="kj-table">
          <thead>
            <tr>
              <th>{headColumn}</th>
              <th>Задача</th>
              <th>Ответственный</th>
              <th>Статус</th>
              <th>Срок</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => (
              <TaskRowView key={task.id} task={task} kind={kind} headLabel={headColumn} />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function TaskAttention() {
  const report = useAttention();

  if (report.isLoading || report.isError) {
    return <QueryState query={report} what="отчёт" />;
  }

  const data = report.data;
  if (!data) return null;

  if (data.total === 0) {
    return (
      <EmptyState
        icon="CheckCircle"
        title="Ни одна задача не выбилась из срока"
        description="Здесь оказываются задачи, у которых срок уже начался, но статус всё ещё Not started, и задачи, у которых срок прошёл, а работа не закончена. Пока таких нет."
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <Icon name="AlertTriangle" size={18} color="var(--burgundy-700)" />
        <span style={{ font: '400 14px var(--font-text)', color: 'var(--text-muted)' }}>
          {`${data.total} ${plural(data.total, 'задача', 'задачи', 'задач')} разошлись с планом на ${formatDate(data.asOf)}. Фильтры на этот вид не действуют.`}
        </span>
      </div>

      <Group
        title="Срок вышел, не закрыты"
        explanation="Срок прошёл, а задача всё ещё Not started или In progress. Слева — сколько дней идёт просрочка."
        tasks={data.overdue}
        kind="overdue"
        headColumn="Просрочка"
      />

      <Group
        title="Срок идёт, не приступили"
        explanation="Срок уже начался, но статус остался Not started. Слева — сколько дней прошло с начала срока."
        tasks={data.notStarted}
        kind="not_started"
        headColumn="Простой"
      />
    </div>
  );
}
